import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchCars } from '../api/api';
import CarTile from '../features/cars/CarTile';

import SEO from '../components/SEO';
import { Helmet } from 'react-helmet-async';
import heroVideo from '../assets/videos/hero.mp4';

import ContactForm from '../components/ContactForm';
export default function HomePage() {
    const [featuredCars, setFeaturedCars] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchCars().then(data => {
            const cars = data.results || data;
            setFeaturedCars(cars.slice(0, 4));
            setLoading(false);
        }).catch(err => {
            console.error("Failed to fetch cars", err);
            setLoading(false);
        });
    }, []);

    return (
        <div className="bg-white text-gray-900 font-sans selection:bg-red-100 selection:text-red-900">
            <Helmet>
                <link rel="preconnect" href="https://images.unsplash.com" />
            </Helmet>
            <SEO
                title="Auta z Niemiec i Austrii"
                description="Import aut z Niemiec i Austrii - sprawdzony dostawca z Podlasia. Samochody osobowe i dostawcze z gwarancją. Suwałki, Augustów, Bakałarzewo."
                schema={{
                    "@context": "https://schema.org",
                    "@type": "AutoDealer",
                    "name": "Cars4Sell Import",
                    "description": "Import aut z Niemiec i Austrii. Sprawdzony dostawca samochodów osobowych i dostawczych z Podlasia.",
                    "url": "https://cars4sell.pl",
                    "logo": "https://cars4sell.pl/c4s-logo.png",
                    "image": "https://cars4sell.pl/c4s-logo.png",
                    "telephone": "+48538194234",
                    "email": "kontakt@cars4sell.pl",
                    "address": {
                        "@type": "PostalAddress",
                        "streetAddress": "Świerkowa 2",
                        "addressLocality": "Bakałarzewo",
                        "postalCode": "16-423",
                        "addressRegion": "podlaskie",
                        "addressCountry": "PL"
                    },
                    "geo": {
                        "@type": "GeoCoordinates",
                        "latitude": "54.1089",
                        "longitude": "22.8456"
                    },
                    "areaServed": [
                        { "@type": "City", "name": "Suwałki" },
                        { "@type": "City", "name": "Augustów" },
                        { "@type": "City", "name": "Ełk" },
                        { "@type": "City", "name": "Bakałarzewo" },
                        { "@type": "City", "name": "Sejny" },
                        { "@type": "AdministrativeArea", "name": "województwo podlaskie" }
                    ],
                    "priceRange": "$$",
                    "openingHoursSpecification": {
                        "@type": "OpeningHoursSpecification",
                        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
                        "opens": "08:00",
                        "closes": "18:00"
                    }
                }}
            />

            {/* Featured Listings (Top Section) */}
            <section className="min-h-[calc(100vh-64px)] md:min-h-[calc(100vh-80px)] py-12 px-6 bg-gray-50 flex flex-col justify-center">
                <div className="max-w-7xl mx-auto w-full">
                    <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-4">
                        <div>
                            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Wybrane oferty</h2>
                            <p className="text-xl text-gray-600">Najciekawsze egzemplarze, które trafiły do naszej oferty.</p>
                        </div>
                        <Link to="/ogloszenia" className="flex items-center gap-2 text-red-700 font-bold hover:text-red-800 transition-colors group">
                            Wszystkie ogłoszenia
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                            </svg>
                        </Link>
                    </div>

                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-3xl border border-gray-100 shadow-sm">
                            <div className="w-16 h-16 border-4 border-red-100 border-t-red-600 rounded-full animate-spin mb-4"></div>
                            <p className="text-gray-500 font-medium">Pobieranie najlepszych ofert...</p>
                        </div>
                    ) : featuredCars.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                            {featuredCars.map(car => (
                                <CarTile key={car.id} car={car} />
                            ))}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-3xl border border-gray-100 shadow-sm">
                            <p className="text-gray-500 font-medium">Obecnie brak wyróżnionych ofert.</p>
                        </div>
                    )}
                </div>
            </section>

            {/* Hero Section */}
            <section className="relative bg-gray-900 text-white min-h-screen flex items-center justify-center overflow-hidden">
                {/* Video Background */}
                <div className="absolute inset-0 z-0">
                    <div className="absolute inset-0 bg-black/60 z-10"></div> {/* Overlay for readability */}
                    <video
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="w-full h-full object-cover"
                        preload="auto"
                        poster="https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?q=80&w=1920&auto=format&fit=crop&fm=webp"
                    >
                        <source src={heroVideo} type="video/mp4" />
                        Your browser does not support the video tag.
                    </video>
                </div>

                <div className="relative z-20 w-full max-w-7xl mx-auto px-6 flex flex-col items-center justify-center py-4 text-center">

                    <div className="text-center animate-fade-in opacity-0" style={{ animationDelay: '0.5s' }}>
                        <h1 className="text-5xl md:text-7xl font-extrabold mb-6 tracking-tight text-white drop-shadow-lg">
                            Cars4Sell <span className="text-red-500">Import</span>
                        </h1>
                        <p className="text-xl md:text-2xl text-gray-200 mb-10 max-w-2xl mx-auto font-light drop-shadow-md">
                            Twoja droga do wymarzonego samochodu zaczyna się tutaj.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                            <Link to="/ogloszenia" className="px-8 py-4 bg-red-600 hover:bg-red-700 text-white font-bold rounded-full transition-all duration-300 hover:shadow-lg hover:-translate-y-1 hover:shadow-red-600/30">
                                Zobacz ofertę
                            </Link>
                            <a href="#contact" className="px-8 py-4 border-2 border-white text-white font-bold rounded-full hover:bg-white hover:text-gray-900 transition-all duration-300">
                                Skontaktuj się
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* Why Us Section */}
            <section className="min-h-screen py-16 px-6 flex flex-col justify-center bg-white">
                <div className="max-w-7xl mx-auto w-full">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Dlaczego my?</h2>
                        <p className="text-xl text-gray-600 max-w-2xl mx-auto">Import aut nie musi być trudny. Z nami to proste i bezpieczne.</p>
                    </div>
                    <div className="grid md:grid-cols-3 gap-8">
                        <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl hover:border-red-100 transition-all duration-300 group">
                            <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-red-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-4">Bezpieczeństwo</h3>
                            <p className="text-gray-600 leading-relaxed">
                                Dokładnie sprawdzamy każdy samochód przed zakupem. Gwarantujemy legalność pochodzenia i stan techniczny.
                            </p>
                        </div>
                        <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl hover:border-red-100 transition-all duration-300 group">
                            <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-red-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-4">Szybkość</h3>
                            <p className="text-gray-600 leading-relaxed">
                                Optymalizujemy proces sprowadzania. Od znalezienia do dostarczenia pod dom - działamy bez zbędnej zwłoki.
                            </p>
                        </div>
                        <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl hover:border-red-100 transition-all duration-300 group">
                            <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-red-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-4">Transparentność</h3>
                            <p className="text-gray-600 leading-relaxed">
                                Żadnych ukrytych kosztów. Jasne zasady współpracy i pełna informacja o stanie technicznym pojazdu.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Process Section */}
            <section className="min-h-screen py-16 px-6 bg-gray-50 flex flex-col justify-center relative overflow-hidden">
                <div className="max-w-7xl mx-auto w-full relative z-10">
                    <div className="grid md:grid-cols-2 gap-16 items-center">
                        <div>
                            <h2 className="text-4xl md:text-5xl font-bold mb-8 text-gray-900">
                                Jak działamy? <br />
                                <span className="text-red-600">Prosto i skutecznie.</span>
                            </h2>
                            <div className="space-y-8">
                                <div className="flex gap-6">
                                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 border border-red-200 flex items-center justify-center text-red-700 font-bold text-xl">1</div>
                                    <div>
                                        <h3 className="text-xl font-bold mb-2 text-gray-900">Analiza potrzeb</h3>
                                        <p className="text-gray-600">Określamy Twoje wymagania: model, budżet, wyposażenie. Słuchamy, by doradzić najlepiej.</p>
                                    </div>
                                </div>
                                <div className="flex gap-6">
                                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 border border-red-200 flex items-center justify-center text-red-700 font-bold text-xl">2</div>
                                    <div>
                                        <h3 className="text-xl font-bold mb-2 text-gray-900">Weryfikacja i Zakup</h3>
                                        <p className="text-gray-600">Sprawdzamy stan techniczny i prawny. Licytujemy lub negocjujemy cenę w Twoim imieniu.</p>
                                    </div>
                                </div>
                                <div className="flex gap-6">
                                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 border border-red-200 flex items-center justify-center text-red-700 font-bold text-xl">3</div>
                                    <div>
                                        <h3 className="text-xl font-bold mb-2 text-gray-900">Transport i Rejestracja</h3>
                                        <p className="text-gray-600">Bezpiecznie sprowadzamy auto i załatwiamy wszelkie formalności. Odbierasz kluczyki.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div id="contact-form-container">
                            <ContactForm />
                        </div>
                    </div>
                </div>
            </section>



        </div>
    );
}